import { DesignedHelper } from "@appally/collections/archive/DesignedHelper";
import { User } from "@appally/collections/User";
import { collections } from "@appally/lib/data/firebase";
import { checkStatus } from "@appally/lib/utils/checkStatus";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { doc, getDoc, setDoc } from "firebase/firestore";

export type UserState = {
  user: null | User | DesignedHelper;
  loaded: boolean;
  loggedIn: boolean;
  helper: null | DesignedHelper;
  updating: boolean;
  supported_user: null | User;
  supporting_users: User[];
  updates: null | User | DesignedHelper;
};

const initialState: UserState = {
  user: null,
  loaded: false,
  loggedIn: false,
  helper: null,
  updating: false,
  supported_user: null,
  supporting_users: [],
  updates: null,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
    },
    setUserLoaded: (state, action) => {
      state.loaded = action.payload;
    },
    setUserLoggedIn: (state, action) => {
      state.loggedIn = action.payload;
    },
    setHelper: (state, action) => {
      state.helper = action.payload;
    },
    setSupportedUser: (state, action) => {
      state.supported_user = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCurrentUser.pending, () => {})
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(updateDBUser.pending, (state) => {
        state.updating = true;
      })
      .addCase(updateDBUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.updating = false;
      })
      .addCase(updateThirdPartyUser.pending, (state) => {
        console.log("updating");
        state.updating = true;
      })
      .addCase(updateThirdPartyUser.fulfilled, (state, action) => {
        console.log("updated", action.payload);
        state.updating = !!action.payload;
      })
      .addCase(getSupportedUser.pending, () => {})
      .addCase(getSupportedUser.fulfilled, (state, action) => {
        if (action.payload) {
          state.supporting_users = [...state.supporting_users, action.payload];
        } else {
          state.supporting_users = [...state.supporting_users];
        }
      })
      .addCase(getSupportedUsers.pending, () => {})
      .addCase(getSupportedUsers.fulfilled, (state, action) => {
        if (action.payload) {
          state.supporting_users = action.payload;
        }
      })
      .addCase(getHelperUser.pending, () => {})
      .addCase(getHelperUser.fulfilled, (state, action) => {
        state.helper = action.payload;
      })
      .addCase(checkForUpdates.pending, () => {
        console.log("checking for user updates");
      })
      .addCase(checkForUpdates.fulfilled, (state, action) => {
        state.updates = action.payload;
      });
  },
});

type GetCurrentUserData = string;

export const getCurrentUser = createAsyncThunk(
  "user/getCurrentUser",
  async (id: GetCurrentUserData): Promise<User | DesignedHelper | null> => {
    const userDocRefAssessmentUsers = doc(collections.users, id);
    const userDocRefDesignatedHelpers = doc(collections.designated_helpers, id);

    try {
      const assessmentUsersDoc = await getDoc(userDocRefAssessmentUsers);

      if (assessmentUsersDoc.exists()) {
        const data = assessmentUsersDoc.data();
        localStorage.setItem("sita_user", JSON.stringify(data));
        return data;
      }

      // Document does not exist in "assessment_users", check "designated_helpers"
      const designatedHelpersDoc = await getDoc(userDocRefDesignatedHelpers);

      if (designatedHelpersDoc.exists()) {
        // Document exists in "designated_helpers", set user accordingly
        const data = designatedHelpersDoc.data();
        localStorage.setItem(`sita_user`, JSON.stringify(data));

        return data;
      }
      return null;
    } catch (error) {
      return null;
    }
  }
);

type UpdateDBUserData = DesignedHelper | User;

export const updateDBUser = createAsyncThunk(
  "user/updateDBUser",
  async (data: UpdateDBUserData) => {
    let table =
      "designated_helper" in data && data.designated_helper
        ? collections.designated_helpers
        : collections.users;

    const docRef = doc(table, data.id);
    const docSnap = await getDoc(docRef);

    data.last_saved = Date.now();

    if (docSnap.exists()) {
      try {
        await setDoc(docRef, data);

        localStorage.setItem("sita_user", JSON.stringify(data));

        return data;
      } catch (e) {
        console.log("error saving to db", e);
        return null;
      }
    }
    return null;
  }
);

type UpdateThirdPartyUserData = DesignedHelper | User;

export const updateThirdPartyUser = createAsyncThunk(
  "user/updateThirdPartyUser",
  async (data: UpdateThirdPartyUserData) => {
    let table =
      "designated_helper" in data && data.designated_helper
        ? collections.designated_helpers
        : collections.users;

    const docRef = doc(table, data.id);
    const docSnap = await getDoc(docRef);

    data.last_saved = Date.now();

    if (docSnap.exists()) {
      try {
        await setDoc(docRef, data);
        return data;
      } catch (e) {
        console.log("error saving to db", e);
        return null;
      }
    }
    return null;
  }
);

export const getSupportedUser = createAsyncThunk(
  "user/getSupportedUser",
  async (id: string) => {
    const docRef = doc(collections.users, id);
    const docSnap = await getDoc(docRef);

    try {
      if (docSnap.exists()) {
        let user = docSnap.data();

        return user;
      }
      return null;
    } catch (error) {
      return null;
    }
  }
);

type GetSupportedUsersData = NonNullable<DesignedHelper["designated_users"]>;

export const getSupportedUsers = createAsyncThunk(
  "user/getSupportedUsers",
  async (userIds: GetSupportedUsersData) => {
    try {
      let userArray = [];
      for (const userId of userIds) {
        try {
          const docRef = doc(collections.users, userId);
          const docSnap = await getDoc(docRef);
          if (!docSnap.exists()) continue;

          const userData = {
            ...docSnap.data(),
            assessmentStatus: checkStatus(docSnap.data().assessment),
          };

          userArray.push(userData);
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      }

      return userArray;
    } catch (error) {
      console.log("error", error);
      return null;
    }
  }
);

export const getHelperUser = createAsyncThunk(
  "user/getHelperUser",
  async (id: string) => {
    const docRef = doc(collections.designated_helpers, id);
    const docSnap = await getDoc(docRef);

    try {
      if (docSnap.exists()) {
        let user = docSnap.data();

        return user;
      }
      return null;
    } catch (error) {
      return null;
    }
  }
);

export const checkForUpdates = createAsyncThunk(
  "user/checkForUpdates",
  async (id: string) => {
    const userDocRefAssessmentUsers = doc(collections.users, id);
    const userDocRefDesignatedHelpers = doc(collections.designated_helpers, id);

    try {
      const assessmentUsersDoc = await getDoc(userDocRefAssessmentUsers);

      if (assessmentUsersDoc.exists()) {
        const data = assessmentUsersDoc.data();

        return data;
      }

      // Document does not exist in "assessment_users", check "designated_helpers"
      const designatedHelpersDoc = await getDoc(userDocRefDesignatedHelpers);

      if (designatedHelpersDoc.exists()) {
        // Document exists in "designated_helpers", set user accordingly
        const data = designatedHelpersDoc.data();

        return data;
      }
      return null;
    } catch (error) {
      return null;
    }
  }
);

export const {
  setUser,
  setUserLoaded,
  setUserLoggedIn,
  setHelper,
  setSupportedUser,
} = userSlice.actions;

export default userSlice.reducer;
